import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Tab, Tabs, Badge, Spinner, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { authAPI, voiceAPI } from '../services/api';
import { getStoredUser, clearAuthData } from '../utils/auth';

function Dashboard() {
  const [user, setUser] = useState(getStoredUser());
  const [myModels, setMyModels] = useState([]);
  const [myLicenses, setMyLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch user profile
      const profileRes = await authAPI.getProfile();
      setUser(profileRes.data);
      localStorage.setItem('user', JSON.stringify(profileRes.data));

      // Fetch user's models and licenses in parallel
      const [modelsRes, licensesRes] = await Promise.all([
        voiceAPI.getMyModels(),
        voiceAPI.getMyLicenses()
      ]);

      setMyModels(modelsRes.data.models || []);
      setMyLicenses(licensesRes.data.licenses || []);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError(err.response?.data?.error || 'Failed to load dashboard data');

      // If unauthorized, redirect to login
      if (err.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    clearAuthData();
    navigate('/login');
  };

  if (loading) {
    return (
      <Container className="text-center" style={{ marginTop: '100px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-3">Loading your dashboard...</p>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* User Profile Header */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Body>
              <Row className="align-items-center">
                <Col md={8}>
                  <h3>Welcome back, {user?.username || 'User'}!</h3>
                  <p className="text-muted mb-2">
                    {user?.email}
                    {user?.role && <Badge bg="info" className="ms-2">{user.role}</Badge>}
                  </p>
                  {user?.wallet_address && (
                    <p className="text-muted small mb-0">
                      <strong>Wallet:</strong> {user.wallet_address.substring(0, 10)}...{user.wallet_address.substring(user.wallet_address.length - 8)}
                    </p>
                  )}
                </Col>
                <Col md={4} className="text-md-end">
                  <Button variant="outline-primary" size="sm" className="me-2" onClick={() => navigate('/profile')}>
                    Edit Profile
                  </Button>
                  <Button variant="outline-danger" size="sm" onClick={handleLogout}>
                    Logout
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Stats Cards */}
      <Row className="mb-4">
        <Col md={4}>
          <Card className="text-center">
            <Card.Body>
              <h2 className="text-primary">{user?.stats?.models_created || 0}</h2>
              <p className="text-muted mb-0">Voice Models Created</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="text-center">
            <Card.Body>
              <h2 className="text-success">{user?.stats?.licenses_purchased || 0}</h2>
              <p className="text-muted mb-0">Licenses Purchased</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="text-center">
            <Card.Body>
              <h2 className="text-info">{user?.stats?.total_usage || 0}</h2>
              <p className="text-muted mb-0">Total Usage</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Tabs for Models and Licenses */}
      <Tabs defaultActiveKey="models" className="mb-3">
        <Tab eventKey="models" title={`My Voice Models (${myModels.length})`}>
          <Row>
            {myModels.length === 0 ? (
              <Col>
                <Card className="text-center p-5">
                  <Card.Body>
                    <h5>No Voice Models Yet</h5>
                    <p className="text-muted">Create your first voice model to get started</p>
                    <Button variant="primary" onClick={() => navigate('/create-model')}>
                      Create Voice Model
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            ) : (
              myModels.map((model) => (
                <Col md={6} lg={4} key={model.id} className="mb-3">
                  <Card className="h-100">
                    <Card.Body>
                      <h5>{model.name}</h5>
                      <p className="text-muted small">{model.description?.substring(0, 100)}...</p>
                      <div className="d-flex justify-content-between align-items-center">
                        <Badge bg={model.status === 'READY' ? 'success' : 'warning'}>
                          {model.status}
                        </Badge>
                        <span className="text-primary fw-bold">${model.base_price}</span>
                      </div>
                      <div className="mt-3">
                        <small className="text-muted">
                          {model.usage_count || 0} uses • {model.rating?.toFixed(1) || 'N/A'} ⭐
                        </small>
                      </div>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="w-100 mt-3"
                        onClick={() => navigate(`/model/${model.id}`)}
                      >
                        View Details
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              ))
            )}
          </Row>
        </Tab>

        <Tab eventKey="licenses" title={`My Licenses (${myLicenses.length})`}>
          <Row>
            {myLicenses.length === 0 ? (
              <Col>
                <Card className="text-center p-5">
                  <Card.Body>
                    <h5>No Licenses Yet</h5>
                    <p className="text-muted">Browse the marketplace to find voice models to license</p>
                    <Button variant="primary" onClick={() => navigate('/marketplace')}>
                      Browse Marketplace
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            ) : (
              myLicenses.map((license) => (
                <Col md={6} lg={4} key={license.id} className="mb-3">
                  <Card className="h-100">
                    <Card.Body>
                      <h6>{license.voice_model?.name || 'Voice Model'}</h6>
                      <Badge bg="success" className="mb-2">{license.license_type}</Badge>
                      <p className="text-muted small mb-2">
                        Purchased: {new Date(license.created_at).toLocaleDateString()}
                      </p>
                      <p className="text-muted small mb-2">
                        Usage: {license.usage_count || 0} / {license.usage_limit || 'Unlimited'}
                      </p>
                      {license.expiry_date && (
                        <p className="text-muted small">
                          Expires: {new Date(license.expiry_date).toLocaleDateString()}
                        </p>
                      )}
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="w-100 mt-2"
                        onClick={() => navigate(`/model/${license.voice_model_id}`)}
                      >
                        Use Model
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              ))
            )}
          </Row>
        </Tab>
      </Tabs>

      {/* Quick Actions */}
      <Row className="mt-4">
        <Col>
          <Card>
            <Card.Body>
              <h5 className="mb-3">Quick Actions</h5>
              <div className="d-flex flex-wrap gap-2">
                <Button variant="primary" onClick={() => navigate('/marketplace')}>
                  Browse Marketplace
                </Button>
                {user?.role === 'creator' && (
                  <Button variant="success" onClick={() => navigate('/create-model')}>
                    Create Voice Model
                  </Button>
                )}
                <Button variant="outline-secondary" onClick={() => navigate('/profile')}>
                  Edit Profile
                </Button>
                {!user?.wallet_address && (
                  <Button variant="outline-warning" onClick={() => navigate('/connect-wallet')}>
                    Connect Wallet
                  </Button>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default Dashboard;
